import React from 'react';

import appendQuery from 'append-query';
import _ from 'lodash';
import PropTypes from 'prop-types';

// Utils

import { Link } from '@nerdwallet/currency';
import { getCreditScoreRangeByValue } from '@nerdwallet/mortgage-utils/rate-adjustment';
import { Num as num, Tracking } from '@nerdwallet/nw-client-lib';
import { ElementInteraction } from '@nerdwallet/nw-client-lib/tracking';

import transformToPlInputs from '../../lib/transform-to-pl-inputs';

// CSS
require('./dti-too-high-modal.less');

const CREDIT_SCORE_POOR = 'POOR';
const CREDIT_SCORE_AVERAGE = 'AVERAGE';
const PL_CONSOLIDATE = 'CONSOLIDATE_DEBT';
// supress "your down payment is too high for low incomes"
const DP_TOO_HIGH_INCOME = 20000;
const DP_TOO_HIGH_DOWN_PAYMENT_PERCENT = 0.2;

const { formOpen } = Tracking;
const FORM_NAME = 'UnaffordableModal';

class DTITooHighModal extends React.Component {
  getIncomeGoal() {
    return (
      _.get(this.props.data, 'adjustedOutputs.error.annualIncome') -
      _.get(this.props.data, 'inputs.income', 0)
    );
  }

  getDebtGoal() {
    return (
      _.get(this.props.data, 'inputs.monthlyDebt', 0) -
      _.get(this.props.data, 'adjustedOutputs.error.monthlyDebt')
    );
  }

  getCreditScoreRange() {
    return _.get(
      getCreditScoreRangeByValue(
        _.get(this.props.data, 'inputs.creditScoreRangeValue')
      ),
      'text'
    ).toUpperCase();
  }

  isDownPaymentTooHigh() {
    return (
      _.get(this.props.data, 'adjustedOutputs.downPaymentPercent') >
        DP_TOO_HIGH_DOWN_PAYMENT_PERCENT &&
      _.get(this.props.data, 'inputs.income') > DP_TOO_HIGH_INCOME
    );
  }

  renderBalanceTransfer() {
    const creditScore = this.getCreditScoreRange();
    if (creditScore === CREDIT_SCORE_POOR) {
      return null;
    }

    let link = '';
    let trackingId = '';

    if (creditScore === CREDIT_SCORE_AVERAGE) {
      link = appendQuery('/balance-transfer-credit-cards', {
        credit_score: 'average',
      });
      trackingId = 'dti-too-high-balance_transfer_good';
    } else {
      link =
        '/best/credit-cards/balance-transfer#purchasestransfers-latefee-waiver';
      trackingId = 'dti-too-high-balance_transfer_average';
    }

    return (
      <div className="option">
        <div className="option-badge">
          <div>3</div>
        </div>
        <div className="option-content">
          <ElementInteraction
            elementDomId={trackingId}
            elementName={trackingId}
            type="CLICK"
          >
            <Link className="option-content-cta" href={link} target="_blank">
              Consolidate debt with a balance transfer credit card
            </Link>
          </ElementInteraction>
        </div>
      </div>
    );
  }

  renderPersonalLoan() {
    const link = appendQuery('/personal-loans', {
      ...transformToPlInputs(this.props.data),
      loanAmountFilter: 10000,
      loanUseFilter: PL_CONSOLIDATE,
    });

    const trackingId = `dti-too-high-${PL_CONSOLIDATE}`;

    return (
      <div className="option">
        <div className="option-badge">
          <div>2</div>
        </div>
        <div className="option-content">
          <ElementInteraction
            elementDomId={trackingId}
            elementName={trackingId}
            type="CLICK"
          >
            <Link className="option-content-cta" href={link} target="_blank">
              Consolidate credit card and other debt with a personal loan
            </Link>
          </ElementInteraction>
        </div>
      </div>
    );
  }

  renderStudentLoans() {
    const link = appendQuery('/refinancing-student-loans/results', {
      annualIncomeFilter: _.get(this.props.data, 'inputs.income'),
      stateFilter: _.get(this.props.data, 'inputs.purchaseAddress.state'),
    });

    const trackingId = 'dti-too-high-STUDENT_LOANS';

    return (
      <div className="option">
        <div className="option-badge">
          <div>1</div>
        </div>
        <div className="option-content">
          <ElementInteraction
            elementDomId={trackingId}
            elementName={trackingId}
            type="CLICK"
          >
            <Link className="option-content-cta" href={link} target="_blank">
              Refi and save on student loans
            </Link>
          </ElementInteraction>
        </div>
      </div>
    );
  }

  renderTitle() {
    const debtGoal = this.getDebtGoal();
    if (debtGoal > 0) {
      return (
        <h3 className="title">Debt is locking you out of homeownership</h3>
      );
    }

    if (this.isDownPaymentTooHigh()) {
      return (
        <h3 className="title">
          Based on your income, property taxes are too high
        </h3>
      );
    }

    return <h3 className="title">Home buying tips</h3>;
  }

  renderOptions() {
    const debtGoal = this.getDebtGoal();
    if (debtGoal > 0) {
      return (
        <React.Fragment>
          <p className="sub-title">What you can do about it:</p>
          <div className="options">
            {this.renderStudentLoans()}
            {this.renderPersonalLoan()}
            {this.renderBalanceTransfer()}
          </div>
        </React.Fragment>
      );
    }

    if (this.isDownPaymentTooHigh()) {
      return null;
    }

    return (
      <p className="sub-title">
        Based on your income and debt, homeownership is currently out of reach.
        To get ready to buy a home, we recommend learning about{' '}
        <Link
          href="https://www.nerdwallet.com/blog/finance/consolidate-debt/"
          target="_blank"
        >
          debt consolidation
        </Link>{' '}
        and figuring out if it is right for you.
      </p>
    );
  }

  renderMessage() {
    const debtGoal = this.getDebtGoal();

    if (debtGoal > 0) {
      return (
        <div className="dti-too-high-footer">
          You should decrease your debt by{' '}
          {num.formatCurrency(this.getDebtGoal())} or increase your annual
          income by {num.formatCurrency(this.getIncomeGoal())} to buy a home.
        </div>
      );
    }

    if (this.isDownPaymentTooHigh()) {
      return (
        <div className="dti-too-high-footer">
          At your current income, property taxes would exceed your earnings.
          Adjust your down payment down to see what you can sustainably afford.
        </div>
      );
    }

    return (
      <div className="dti-too-high-footer">
        You should increase your annual income by{' '}
        {num.formatCurrency(this.getIncomeGoal())} to buy a home.
      </div>
    );
  }

  render() {
    if (_.get(this.props.data, 'adjustedOutputs.error')) {
      // Log whether the error was shown.
      if (typeof window !== 'undefined') {
        formOpen({
          formName: FORM_NAME,
        });
      }

      return (
        <div className="dti-too-high-wrapper">
          <div className="dti-too-high-content">
            {this.renderTitle()}
            {this.renderOptions()}
            {this.renderMessage()}
          </div>
        </div>
      );
    }

    return null;
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
DTITooHighModal.displayName = 'DTITooHighModal';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
DTITooHighModal.propTypes = {
  data: PropTypes.shape({
    adjustedOutputs: PropTypes.object.isRequired,
    inputs: PropTypes.object.isRequired,
    outputs: PropTypes.object.isRequired,
  }).isRequired,
};

export default DTITooHighModal;
